import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, X } from 'lucide-react';
import { useSiteContent } from '@/context/SiteContentContext';
import { Button } from '@/components/ui/button';
import DynamicIcon from '@/components/common/DynamicIcon';
import { cn } from '@/lib/utils';
import { PageHero } from '@/components/common/PageHero';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { PageSidebar } from '@/components/common/PageSidebar';
import { SEOHead } from '@/components/seo/SEOHead';
import { usePageDesign } from '@/hooks/usePageDesign';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

export const ServicesPage: React.FC = () => {
  const { services, faq, template, seoCanonical } = useSiteContent();
  const [selectedService, setSelectedService] = useState<string | null>(null);
  const { heroStyle, showBreadcrumbs, showTrustBadges, showCtaButtons, showFaqSection } = usePageDesign('services_listing');

  const currentService = services.find(s => s.slug === selectedService);
  const brandName = template.brand?.name || 'Our Company';

  // Generate related links for sidebar
  const relatedLinks = services.slice(0, 5).map(service => ({
    title: service.title,
    href: `/services#${service.slug}`,
    icon: service.icon,
  }));

  return (
    <>
      <SEOHead
        title={`Professional Services | ${brandName}`}
        description={`Explore our full range of professional services. ${brandName} delivers quality workmanship with licensed, insured experts. Get a free estimate today.`}
        canonical={seoCanonical ? `${seoCanonical}/services` : undefined}
        includeFAQ
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <ConfigurableHero
          heroStyle={heroStyle}
          title="Our Services"
          subtitle="Professional services delivered with quality, expertise, and care. We're committed to exceeding your expectations on every project."
          badge="Licensed & Insured"
          showBreadcrumbs={showBreadcrumbs}
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Services' }]}
          showTrustBadges={showTrustBadges}
          showCtaButtons={showCtaButtons}
          showRating
          rating={template.stats?.googleRating || 4.9}
          reviewCount={template.stats?.reviewCount || 100}
        />

        {/* Services Grid with Sidebar */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Main Content - Services List */}
              <main className="lg:col-span-2" role="main">
                <h2 className="sr-only">All Services</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {services.map((service, index) => (
                    <motion.article
                      key={service.slug}
                      id={service.slug}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className={cn(
                        'group p-6 bg-card rounded-xl border border-border cursor-pointer transition-all duration-300',
                        'hover:border-primary/50 hover:shadow-lg'
                      )}
                      onClick={() => setSelectedService(service.slug)}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                          <DynamicIcon name={service.icon} className="h-7 w-7 text-primary group-hover:text-primary-foreground" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-heading font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                            {service.title}
                          </h3>
                          <p className="text-sm text-muted-foreground mb-3">{service.description}</p>
                          {service.features && (
                            <ul className="space-y-1">
                              {service.features.slice(0, 3).map((feature, i) => (
                                <li key={i} className="text-xs text-muted-foreground flex items-center gap-1.5">
                                  <CheckCircle className="h-3 w-3 text-primary" />
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </motion.article>
                  ))}
                </div>
              </main>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-28">
                  <PageSidebar 
                    showCta
                    showTrustBadges
                    showPromo
                    showServiceAreas
                    ctaTitle="Need a Quote?"
                    ctaDescription="Contact us today for a free, no-obligation estimate."
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Detail Modal */}
        <AnimatePresence>
          {selectedService && currentService && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/50"
              onClick={() => setSelectedService(null)}
            >
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="bg-card rounded-2xl p-8 max-w-lg w-full max-h-[80vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="service-modal-title"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                      <DynamicIcon name={currentService.icon} className="h-7 w-7 text-primary" />
                    </div>
                    <h2 id="service-modal-title" className="font-heading text-2xl font-bold">
                      {currentService.title}
                    </h2>
                  </div>
                  <button
                    onClick={() => setSelectedService(null)}
                    className="p-2 hover:bg-muted rounded-lg transition-colors"
                    aria-label="Close dialog"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <p className="text-muted-foreground mb-6">{currentService.description}</p>
                
                {currentService.features && (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-3">What's Included</h4>
                    <ul className="space-y-2">
                      {currentService.features.map((feature, i) => (
                        <li key={i} className="flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button asChild className="flex-1">
                    <Link to="/contact">Get Quote</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <a href={`tel:${template.contact?.phone?.replace(/\D/g, '') || ''}`}>
                      <Phone className="h-4 w-4 mr-2" />
                      Call Now
                    </a>
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* FAQ Section */}
        {faq.length > 0 && (
          <section id="faq" className="py-16 lg:py-24 bg-muted" aria-labelledby="faq-heading">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto">
                <div className="text-center mb-12">
                  <h2 id="faq-heading" className="font-heading text-3xl lg:text-4xl font-bold mb-4">
                    Service FAQs
                  </h2>
                  <p className="text-muted-foreground">Common questions about our services.</p>
                </div>

                <Accordion type="single" collapsible className="space-y-4">
                  {faq.map((item, index) => (
                    <AccordionItem
                      key={index}
                      value={`faq-${index}`}
                      className="bg-card rounded-xl border border-border px-6"
                    >
                      <AccordionTrigger className="text-left font-medium py-4 hover:no-underline">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground pb-4">
                        {item.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 bg-primary text-primary-foreground" aria-labelledby="cta-heading">
          <div className="container mx-auto px-4 text-center">
            <h2 id="cta-heading" className="font-heading text-3xl lg:text-4xl font-bold mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto">
              Contact us today for a free consultation and estimate.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild variant="secondary" size="lg">
                <Link to="/contact">Get Free Estimate</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="bg-transparent border-primary-foreground/30 hover:bg-primary-foreground/10">
                <a href={`tel:${template.contact?.phone?.replace(/\D/g, '') || ''}`}>
                  Call {template.contact?.phone || 'Now'}
                </a>
              </Button>
            </div>
          </div>
        </section>
      </motion.div>
    </>
  );
};
